import { deserializeXYWH } from '@blocksuite/global/gfx';
import type { Store } from '@blocksuite/store';
import {
  createWriterShardPrefix,
  encodePageStructureEntry,
  PAGE_LARGE_DATA_POLICY,
  PageStructureWriteError,
  planPageElementPart,
  readPageFormatVersion,
} from '@streamlain/database-identity';
import * as Y from 'yjs';

import type { SurfaceBlockTransformer } from './surface-transformer.js';

export { snapshotMapRecord } from './surface-transformer.js';

/** A serialized canvas element as it is about to be written. */
export type CompositionElement = Record<string, unknown> & {
  id: string;
  type: string;
  xywh: string;
  index: string;
};

/** A mind map with the nodes that are written together with it. */
export interface MindmapComposition {
  mindmap: CompositionElement;
  members: readonly CompositionElement[];
}

/**
 * Checks, before anything is written, that every mind map fits one page part
 * together with its nodes: the first node is planned like the real write, the
 * nodes and the mind map are measured with the part directory's own byte
 * measure. Throws `page-part-unit-full` when one does not fit.
 */
export function assertMindmapCompositionsFit(
  store: Store,
  surfaceId: string,
  compositions: readonly MindmapComposition[]
): void {
  const planned = compositions.filter(item => item.members.length > 0);
  if (!planned.length) return;
  const structure = store.pageStructure;
  const directory = structure.planningDirectory(false);
  const transformer = store.schema.get('affine:surface')!.transformer!(
    new Map()
  ) as SurfaceBlockTransformer;
  for (const { mindmap, members } of planned) {
    const plan = planPageElementPart({
      formatVersion: readPageFormatVersion(store.spaceDoc),
      rect: deserializeXYWH(members[0].xywh),
      entries: structure.index.entries(),
      directory,
      writerPrefix: createWriterShardPrefix(store.spaceDoc.clientID),
    });
    const measured = new Y.Doc();
    try {
      const fields = measured.getMap('elements');
      const entries = measured.getMap('pageStructure');
      for (const data of [...members, mindmap]) {
        fields.set(data.id, transformer.elementInput(data.id, data).fields);
        entries.set(
          data.id,
          encodePageStructureEntry({
            k: data.type,
            p: surfaceId,
            o: data.index,
            s: plan.partId,
          })
        );
      }
      const b =
        (directory.get(plan.partId)?.b ?? 0) +
        structure.measureSnapshotBytes(measured);
      if (b >= PAGE_LARGE_DATA_POLICY.part.hardBytes)
        throw new PageStructureWriteError(
          'Mindmap insertion exceeds page part',
          'page-part-unit-full'
        );
      // The part grows by the hull of the whole composition, as measurePart does.
      const rects = [
        plan.rect,
        ...members.map(data => deserializeXYWH(data.xywh)),
      ];
      const x = Math.min(...rects.map(r => r[0]));
      const y = Math.min(...rects.map(r => r[1]));
      directory.set(plan.partId, {
        b,
        r: [
          x,
          y,
          Math.max(...rects.map(r => r[0] + r[2])) - x,
          Math.max(...rects.map(r => r[1] + r[3])) - y,
        ],
      });
    } finally {
      measured.destroy();
    }
  }
}
